import { GameConstants } from "@common/constants";
import { GameConsole } from "../console/gameConsole";
import { wayInOffer } from "../utils/entrances";
import { t, tr } from "../utils/translations/phrases";
import { PingWatch, TickWatch } from "./lagSmoothingLogic";

/**
 * Lag smoothing (the rules are in `lagSmoothingLogic.ts`): it switches itself on once the ping has stayed above 180 ms
 * for more than 10 seconds, and off once it has been back at or under 180 ms for as long. While it's on, every moving
 * object (players, the camera with yours, loot, grenades, hares...) is eased from where it's drawn towards each new
 * position over a little more than the usual time between the server's updates, so an uneven connection shows as
 * steady movement instead of jumps and stops (`GameObject`). It costs a few hundredths of a second of extra delay, and
 * only applies with the Movement smoothing setting on. A note under the minimap says when it's on, with the ping and,
 * when the menu found one clearly faster for this player, an entrance to play through instead.
 */
class LagSmoothingManager {
    private readonly _ping = new PingWatch();
    private readonly _ticks = new TickWatch(1000 / GameConstants.tps);

    private _smoothing = false;
    /** whether new positions are smoothed now: switched on, and the player's Movement smoothing setting is on */
    get smoothing(): boolean { return this._smoothing; }

    /** how long each new position is eased in over while smoothing (ms) */
    get window(): number { return this._ticks.window; }

    /** whether it's switched on (the ping has been high) */
    get on(): boolean { return this._ping.on; }

    /** a server update arrived (before its objects take their new positions) */
    noteUpdate(at: number): void {
        this._ticks.add(at);
        this._smoothing = this._ping.on && GameConsole.getBuiltInCVar("cv_movement_smoothing");
    }

    private _shownAt = 0;

    /** one ping measurement (an input the server answered) */
    notePing(ms: number, at: number): void {
        if (this._ping.add(ms, at)) {
            console.log(
                this._ping.on
                    ? `[lag smoothing] on: the ping has stayed above 180 ms for 10 s (${Math.round(this._ping.level ?? ms)} ms)`
                    : `[lag smoothing] off: the ping has been under 180 ms for 10 s (${Math.round(this._ping.level ?? ms)} ms)`
            );
            this._render();
        } else if (this._ping.on && at - this._shownAt >= 1_000) {
            this._render();
        }
    }

    /** a new game: it starts off again (the connection may have changed) */
    reset(): void {
        this._ping.reset();
        this._ticks.reset();
        this._smoothing = false;
        this._render();
    }

    private _render(): void {
        this._shownAt = Date.now();
        const note = document.getElementById("lag-smoothing-note");
        if (!note) return;
        // only said while it smooths anything: with Movement smoothing off in the settings, it doesn't
        note.hidden = !(this._ping.on && GameConsole.getBuiltInCVar("cv_movement_smoothing"));
        if (note.hidden) return;

        const ping = note.querySelector<HTMLElement>(".lsn-ping");
        if (ping) ping.textContent = this._ping.level === undefined ? "" : `${Math.round(this._ping.level)} ms`;

        // a way in that's clearly faster for this player (the menu timed them), if there is one
        const offer = wayInOffer();
        const tip = note.querySelector<HTMLElement>(".lsn-entrance");
        if (tip) {
            tip.hidden = offer === undefined;
            const address = tip.querySelector<HTMLElement>(".lsn-entrance-url");
            const which = tip.querySelector<HTMLElement>(".lsn-entrance-name");
            if (address && offer) address.textContent = offer.url.replace(/^https?:\/\//u, "");
            if (which && offer) which.textContent = offer.name === "main"
                ? t("the main address, <ping> ms", { ping: offer.ping })
                : t("the <name> entrance, <ping> ms", { name: tr(offer.name), ping: offer.ping });
        }
    }
}

export const LagSmoothing = new LagSmoothingManager();
